import { ReactNode } from "react";
import styled from "styled-components";
import { LeftOutlined } from "@ant-design/icons";
import { colors } from "../app/styles";

interface StepShellProps {
  title: ReactNode;
  subtitle?: ReactNode;
  onBack?: () => void;
  children: ReactNode;
  /** Full-width bottom-anchored action bar (buttons). */
  actions?: ReactNode;
}

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
`;

const Head = styled.div`
  flex: 0 0 auto;
  padding: 14px 16px 8px;
  display: flex;
  align-items: flex-start;
  gap: 10px;
`;

const BackButton = styled.button`
  flex: 0 0 auto;
  height: 36px;
  width: 36px;
  border: none;
  border-radius: 10px;
  background: #ffffff;
  box-shadow: 0px 1px 3px rgba(0, 0, 0, 0.12);
  color: ${colors.primaryDark};
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Titles = styled.div`
  min-width: 0;
`;

const Title = styled.h1`
  margin: 0;
  font-weight: 800;
  font-size: 19px;
  color: ${colors.primaryDark};
  line-height: 1.2;
`;

const Subtitle = styled.div`
  font-size: 13px;
  color: ${colors.textMuted};
  margin-top: 2px;
`;

const Body = styled.div`
  flex: 1 1 auto;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 12px 16px 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ActionBar = styled.div`
  flex: 0 0 auto;
  padding: 12px 16px calc(12px + env(safe-area-inset-bottom, 0px));
  background: #ffffff;
  box-shadow: 0px -2px 8px rgba(0, 0, 0, 0.08);
  display: flex;
  gap: 12px;

  & > * {
    flex: 1 1 0;
  }
`;

export default function StepShell({
  title,
  subtitle,
  onBack,
  children,
  actions,
}: StepShellProps) {
  return (
    <Wrap>
      <Head>
        {onBack && (
          <BackButton type="button" aria-label="Back" onClick={onBack}>
            <LeftOutlined />
          </BackButton>
        )}
        <Titles>
          <Title>{title}</Title>
          {subtitle && <Subtitle>{subtitle}</Subtitle>}
        </Titles>
      </Head>
      <Body>{children}</Body>
      {actions && <ActionBar>{actions}</ActionBar>}
    </Wrap>
  );
}
